import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatCurrency } from "@/lib/utils";
import { Banknote, AlertCircle } from "lucide-react";

export default function FinancingSection({ decision, updateDecision, team }) {
  const { t } = useTranslation();
  const cash = team?.cash || 8000000;
  const stDebt = team?.short_term_debt || 2000000;
  const ltDebt = team?.long_term_debt || 18000000;
  const totalEquity = (team?.paid_in_capital || 15000000) + (team?.retained_earnings || 5000000);
  const shares = team?.shares_outstanding || 1000000;
  const stockPrice = team?.stock_price || 20;
  const totalDebt = stDebt + ltDebt;
  const deRatio = totalEquity > 0 ? totalDebt / totalEquity : 0;

  return (
    <div className="space-y-6">
      {/* Financial Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: "Cash", value: formatCurrency(cash) },
          { label: "Total Debt", value: formatCurrency(totalDebt) },
          { label: "Total Equity", value: formatCurrency(totalEquity) },
          { label: "D/E Ratio", value: deRatio.toFixed(2), warn: deRatio > 2.5 },
        ].map(item => (
          <div key={item.label} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
            <p className="text-[10px] text-slate-500 uppercase">{item.label}</p>
            <p className={`font-mono font-semibold text-sm ${item.warn ? "text-rose-600" : "text-slate-800"}`}>{item.value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Borrowing */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle className="flex items-center gap-2"><Banknote className="w-5 h-5" /> {t("decisions.debtLabel")}</CardTitle>
            <CardDescription>{t("decisions.debtDesc", { ratio: deRatio.toFixed(2) })}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div>
              <Label className="text-xs">{t("decisions.shortTermBorrowing")} (8% p.a.)</Label>
              <CurrencyInput value={decision.short_term_borrowing || 0} onChange={v => updateDecision("short_term_borrowing", v)} max={5000000} testId="st-borrow" />
            </div>
            <div>
              <Label className="text-xs">{t("decisions.longTermBorrowing")} (6% p.a.)</Label>
              <CurrencyInput value={decision.long_term_borrowing || 0} onChange={v => updateDecision("long_term_borrowing", v)} max={30000000} testId="lt-borrow" />
            </div>
            <div>
              <Label className="text-xs">{t("decisions.debtRepaymentST")} ({formatCurrency(stDebt)})</Label>
              <CurrencyInput value={decision.debt_repayment_short_term || 0} onChange={v => updateDecision("debt_repayment_short_term", v)} max={stDebt} testId="debt-repay-st" />
            </div>
            <div>
              <Label className="text-xs">{t("decisions.debtRepaymentLT")} ({formatCurrency(ltDebt)})</Label>
              <CurrencyInput value={decision.debt_repayment_long_term || 0} onChange={v => updateDecision("debt_repayment_long_term", v)} max={ltDebt} testId="debt-repay-lt" />
            </div>
          </CardContent>
        </Card>

        {/* Equity & Dividends */}
        <Card>
          <CardHeader className="card-header-flight-deck">
            <CardTitle>{t("decisions.equityDividendsLabel")}</CardTitle>
            <CardDescription>{t("decisions.equityDesc", { price: formatCurrency(stockPrice, 2), shares: shares.toLocaleString() })}</CardDescription>
          </CardHeader>
          <CardContent className="pt-4 space-y-4">
            <div>
              <Label className="text-xs">{t("decisions.shareIssuance")} (max 500,000)</Label>
              <Input type="number" min={0} max={500000} step={10000} value={decision.share_issuance || 0} onChange={e => updateDecision("share_issuance", parseInt(e.target.value) || 0)} className="font-mono h-9 mt-1" data-testid="share-issue" />
              {(decision.share_issuance || 0) > 0 && (
                <p className="text-xs text-sky-600 mt-1">Proceeds: {formatCurrency((decision.share_issuance || 0) * stockPrice)} (dilutes existing shareholders)</p>
              )}
            </div>
            <div>
              <Label className="text-xs">{t("decisions.shareBuyback")} (max 200,000)</Label>
              <Input type="number" min={0} max={200000} step={10000} value={decision.share_buyback || 0} onChange={e => updateDecision("share_buyback", parseInt(e.target.value) || 0)} className="font-mono h-9 mt-1" data-testid="share-buyback" />
              {(decision.share_buyback || 0) > 0 && (
                <p className="text-xs text-amber-600 mt-1">Cost: {formatCurrency((decision.share_buyback || 0) * stockPrice)} (increases EPS)</p>
              )}
            </div>
            <div>
              <Label className="text-xs">{t("decisions.dividend")} (max $2.00)</Label>
              <div className="relative mt-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
                <Input type="number" min={0} max={2} step={0.1} value={decision.dividend_per_share || 0} onChange={e => updateDecision("dividend_per_share", parseFloat(e.target.value) || 0)} className="pl-7 font-mono h-9" data-testid="dividend-input" />
              </div>
              {(decision.dividend_per_share || 0) > 0 && (
                <p className="text-xs text-amber-600 mt-1">Total payout: {formatCurrency((decision.dividend_per_share || 0) * shares)}</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Constraints Warning */}
      {deRatio > 2.5 && (
        <div className="flex items-center gap-2 text-amber-600 bg-amber-50 px-4 py-2 rounded-lg">
          <AlertCircle className="w-4 h-4 shrink-0" />
          <span className="text-sm">D/E ratio approaching limit (3.0). Further borrowing may be restricted.</span>
        </div>
      )}
    </div>
  );
}

function CurrencyInput({ value, onChange, max, testId }) {
  return (
    <div className="relative mt-1">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
      <Input type="number" min={0} max={max} step={100000} value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)} className="pl-7 font-mono h-9" data-testid={testId} />
    </div>
  );
}
